import {
  type ApolloClient,
  ApolloError,
  type ApolloQueryResult,
  NetworkStatus,
} from "@apollo/client";
import {
  GetEventsDocument,
  type GetEventsQuery,
  type GetEventsQueryVariables,
} from "@package/ui-gql/index-analytics";
import { useEffect, useState } from "react";
import { useAnalyticsApolloClient } from "./use-analytics-apollo-client";

type UseGetEventsResponse = ApolloQueryResult<GetEventsQuery> & {
  refetch: (
    refetchVariables?: GetEventsQueryVariables,
  ) => Promise<ApolloQueryResult<GetEventsQuery>>;
};

const defaultQueryResult: ApolloQueryResult<GetEventsQuery> = {
  data: {
    items: [],
    total: {
      __typename: "ListMetadata",
      count: 0,
    },
  },
  loading: false,
  networkStatus: NetworkStatus.ready,
};

export function useGetEvents(
  variables: GetEventsQueryVariables,
  skip = false,
  apolloClient?: ApolloClient<object>,
) {
  const client = useAnalyticsApolloClient(apolloClient);
  const [response, setResponse] = useState<UseGetEventsResponse>({
    ...defaultQueryResult,
    refetch: async () => defaultQueryResult,
  });

  useEffect(
    function effectOnFetchEvents() {
      const fetchEvents = async (
        variablesOverride?: GetEventsQueryVariables,
      ) => {
        try {
          const r = await client?.query<
            GetEventsQuery,
            GetEventsQueryVariables
          >({
            query: GetEventsDocument,
            variables: variablesOverride ?? variables,
          });
          if (r) {
            setResponse({
              ...r,
              refetch: async (refetchVariables?: GetEventsQueryVariables) =>
                (await fetchEvents(refetchVariables)) ?? defaultQueryResult,
            });
          }

          return r;
        } catch (error: unknown) {
          console.error("Error fetching events:", error);
          setResponse((prev) => ({
            ...prev,
            error: new ApolloError({ errorMessage: "Error fetching events" }),
            loading: false,
          }));
        }
      };

      if (!skip) {
        fetchEvents();
      }
    },
    [client, skip, variables],
  );

  return response;
}
